import {
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useMarketplacePartnerRegisterPageContext } from '../components/marketplacePartnerRegisterPageContext'

export type BrandOption = {
  label: string
  value: string
}

type UseMarketplacePartnerRegisterPageAutocompleteStateResult = {
  handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void
  handleOptionClick: (option: BrandOption) => void
  handleRemoveChip: (optionValue: string) => void
  highlightedIndex: number
  inputValue: string
  selectedBrands: BrandOption[]
  selectedValues: Set<string>
  setHighlightedIndex: Dispatch<SetStateAction<number>>
}

export const useMarketplacePartnerRegisterPageAutocompleteState =
  (): UseMarketplacePartnerRegisterPageAutocompleteStateResult => {
    const { formState, resetVersion } =
      useMarketplacePartnerRegisterPageContext()
    formState.use()

    const [selectedBrands, setSelectedBrands] = useState<BrandOption[]>([])
    const [inputValue, setInputValue] = useState('')
    const [highlightedIndex, setHighlightedIndex] = useState(-1)

    const selectedValues = useMemo(
      () => new Set(selectedBrands.map((brand) => brand.value)),
      [selectedBrands],
    )

    const syncFormState = useCallback(
      (brands: BrandOption[]) => {
        formState.setValue(
          brands.map((brand) => brand.label),
          formState.model,
          'brandsDropDown',
        )
      },
      [formState],
    )

    const handleOptionClick = useCallback(
      (option: BrandOption) => {
        const isSelected = selectedValues.has(option.value)
        const newValue = isSelected
          ? selectedBrands.filter((val) => val.value !== option.value)
          : [...selectedBrands, option]

        setSelectedBrands(newValue)
        syncFormState(newValue)
      },
      [selectedBrands, selectedValues, syncFormState],
    )

    const handleRemoveChip = useCallback(
      (optionValue: string) => {
        const newValue = selectedBrands.filter(
          (val) => val.value !== optionValue,
        )
        setSelectedBrands(newValue)
        syncFormState(newValue)
      },
      [selectedBrands, syncFormState],
    )

    const handleInputChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value
        setInputValue(newValue)
        setHighlightedIndex(-1)
      },
      [],
    )

    useEffect(() => {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- don't reference, refactor if touched
      setSelectedBrands([])
      setInputValue('')
      setHighlightedIndex(-1)
    }, [resetVersion])

    return {
      handleInputChange,
      handleOptionClick,
      handleRemoveChip,
      highlightedIndex,
      inputValue,
      selectedBrands,
      selectedValues,
      setHighlightedIndex,
    }
  }
